import { useScreenHelp } from "./ScreenHelp";
// src/CqcSiteOnboarding.js
// "Select the organisations you look after" — the entry point for a new
// organisation. Mirrors the search-then-select pattern SiteManagement.js
// uses for organisations, but talking to the CQC proxy endpoints instead of
// stockcontrol.organisations.
//
// IMPORTANT: CQC's public API has no free-text name/postcode search — only
// exact lookup by CQC Location ID (see cqc_client.js's header comment for
// why, and where the id lives on cqc.org.uk). In mock mode (no
// CQC_SUBSCRIPTION_KEY set yet) the one search field does substring-match
// against the two fixture locations' names, so the demo flow still feels
// like a search — but once wired to the real API, an exact Location ID is
// what actually has to be typed.

import React, { useState } from "react";
import CqcAddressForm from "./CqcAddressForm";
import { cqcApi } from "./cqcApi";
import { RatingBadge } from "./cqcColors";

const S = {
  wrap: { fontFamily: "system-ui, sans-serif", maxWidth: 780, margin: "0 auto", padding: "24px 16px" },
  h1: { fontSize: 20, fontWeight: 600, margin: "0 0 4px" },
  sub: { fontSize: 13, color: "#5D5D5D", margin: "0 0 20px" },
  searchRow: { display: "flex", gap: 8, marginBottom: 16 },
  input: { flex: 1, padding: "9px 12px", fontSize: 14, border: "1px solid #D9D9D9", borderRadius: 6, fontFamily: "inherit" },
  btn: { padding: "9px 16px", fontSize: 14, fontWeight: 600, border: "none", borderRadius: 6,
         background: "#0E8CFF", color: "#fff", cursor: "pointer" },
  btnSecondary: { padding: "9px 16px", fontSize: 14, fontWeight: 600, borderRadius: 6, cursor: "pointer",
                  background: "#fff", color: "#434343", border: "1px solid #D9D9D9" },
  card: { border: "1px solid #F0F0F0", borderRadius: 8, padding: "12px 14px", marginBottom: 8,
          display: "flex", alignItems: "center", gap: 12, background: "#fff" },
  cardSelected: { border: "1px solid #0E8CFF", background: "#E6F7FF" },
  name: { fontWeight: 600, fontSize: 14 },
  meta: { fontSize: 12, color: "#5D5D5D" },
  empty: { fontSize: 13, color: "#8C8C8C", padding: "20px 0", textAlign: "center" },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 },
  err: { color: "#A8071A", fontSize: 13, marginBottom: 12 },
};

export default function CqcSiteOnboarding({ onImported }) {
  const [manual,setManual]=useState(false);
  const [name, setName] = useState("");
  const [results, setResults] = useState([]);
  const detailById = {};
  const [selected, setSelected] = useState(() => new Set());
  const [status, setStatus] = useState("idle"); // idle | searching | importing
  const [error, setError] = useState(null);

  useScreenHelp({ title: "Select organisations for your group", blurb: "Add the care locations you manage before creating their individual service profiles and evidence plans.", can: ["Find an English location by its CQC location ID, or add a location manually using its address.", "Select the locations you look after, then add them to your group."], related: ["care-group", "care-profile"] }, 25);
  async function runSearch(e) {
    e && e.preventDefault();
    if (!name.trim()) { setError("Enter a CQC Location ID."); return; }
    setStatus("searching"); setError(null);
    try {
      const data = await cqcApi.searchLocations(name.trim(), "");
      setResults(prev => [...prev.filter(r => !(data.locations || []).some(n => n.locationId === r.locationId)), ...(data.locations || [])]);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setStatus("idle");
    }
  }

  function toggle(locationId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId); else next.add(locationId);
      return next;
    });
  }

  async function doImport() {
    if (selected.size === 0) return;
    setStatus("importing"); setError(null);
    try {
      const data = await cqcApi.importSites([...selected]);
      if (onImported) await onImported(data.imported || []);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setStatus("idle");
    }
  }

  if(manual)return <CqcAddressForm create onCancel={()=>setManual(false)} onSaved={async site=>{if(onImported)await onImported([site]);}}/>;
  return (
    <div style={S.wrap}>
      <h1 style={S.h1}>Select the organisations you look after</h1>
      <p><button style={S.btn} onClick={()=>setManual(true)}>Add organisation by address — all UK countries</button></p>
      <h3>Import an existing CQC location — England</h3>
      <p style={S.sub}>
        Add each dentist, care home or other service using its CQC Location ID. CQC imports are for England. After selecting
        your organisations, create a separate service profile for each to configure its functions
        and relevant evidence items. Registration information is a starting point; you confirm the evidence plan.
      </p>
      <p style={S.sub}>
        Don't have the ID handy? Search for the site at{" "}
        <a href="https://www.cqc.org.uk/" target="_blank" rel="noreferrer">cqc.org.uk</a>, open its
        page, and copy the ID from the end of the URL — e.g.{" "}
        <code>cqc.org.uk/location/1-XXXXXXXXX</code> → <code>1-XXXXXXXXX</code>. CQC's public API
        doesn't support searching by name or postcode, only exact ID lookup.
      </p>

      <form style={S.searchRow} onSubmit={runSearch}>
        <input style={S.input} placeholder="CQC Location ID, e.g. 1-000000001" value={name}
               onChange={(e) => setName(e.target.value)} />
        <button type="submit" style={S.btn} disabled={status === "searching"}>
          {status === "searching" ? "Looking up…" : "Look up"}
        </button>
      </form>

      {error && <div style={S.err}>{error}</div>}

      {results.length === 0 ? (
        <div style={S.empty}>No results yet — enter a CQC Location ID above.</div>
      ) : (
        <div>
          {results.map((r) => {
            const isSel = selected.has(r.locationId);
            const detail = detailById[r.locationId];
            return (
              <div key={r.locationId} style={{ ...S.card, ...(isSel ? S.cardSelected : {}) }}
                   onClick={() => toggle(r.locationId)}>
                <input type="checkbox" checked={isSel} onChange={() => toggle(r.locationId)}
                       onClick={(e) => e.stopPropagation()} />
                <div style={{ flex: 1 }}>
                  <div style={S.name}>{r.name}</div>
                  <div style={S.meta}>
                    {r.postalCode} · CQC ID {r.locationId}
                    {detail && detail.gacServiceTypes && detail.gacServiceTypes.length > 0
                      ? ` · ${detail.gacServiceTypes.join(", ")}` : ""}
                  </div>
                </div>
                {detail && detail.ratings && <RatingBadge rating={detail.ratings.overall} />}
              </div>
            );
          })}
        </div>
      )}

      <div style={S.footer}>
        <span style={S.meta}>{selected.size} site{selected.size === 1 ? "" : "s"} selected</span>
        <button style={selected.size ? S.btn : S.btnSecondary} disabled={selected.size === 0 || status === "importing"}
                onClick={doImport}>
          {status === "importing" ? "Importing…" : `Import ${selected.size || ""} site${selected.size === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}
